/** Ventana visible de una tarea: fecha_inicio … fecha_vencimiento (fin del plazo). */

function parseTaskMetadata(tarea = {}) {
  if (!tarea?.metadata) return {};
  if (typeof tarea.metadata === "object") return tarea.metadata;
  try {
    return JSON.parse(tarea.metadata);
  } catch {
    return {};
  }
}

export function madridDayKey(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
}

function compareMadridDays(a, b) {
  const ka = madridDayKey(a);
  const kb = madridDayKey(b);
  if (!ka || !kb) return 0;
  if (ka === kb) return 0;
  return ka < kb ? -1 : 1;
}

/** Suma días calendario sobre clave yyyy-mm-dd (Madrid). */
export function addMadridDays(value, days = 0) {
  const key = madridDayKey(value);
  if (!key) return "";
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days, 12, 0, 0));
  return madridDayKey(dt);
}

function madridWeekday(value) {
  const label = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    timeZone: "Europe/Madrid",
  }).format(new Date(value));
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[String(label).slice(0, 3)] ?? 0;
}

function parseTaskCreatedAt(tarea = {}) {
  const candidates = [tarea.created_at, tarea.lead_created_at, tarea.captacion_created_at];
  for (const value of candidates) {
    if (!value) continue;
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function isScheduledStartAfterCreation(startDate, createdAt) {
  const start = new Date(startDate);
  const created = new Date(createdAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(created.getTime())) return false;
  if (madridDayKey(start) !== madridDayKey(created)) return true;
  return start.getTime() - created.getTime() > 60 * 60 * 1000;
}

export function isScheduledExecutionTask(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  const code = String(meta.task_code || "").trim().toUpperCase();
  if (code === "TC") return true;
  const inicio = tarea.fecha_inicio || meta.scheduled_start_at || meta.scheduledStartAt;
  const created = parseTaskCreatedAt(tarea);
  if (inicio && created && isScheduledStartAfterCreation(inicio, created)) return true;
  return false;
}

/** TC/T4 · Cita vinculada a agenda: debe verse en el tablero antes del día de la visita. */
export function isFutureVisitBoardTask(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  const code = String(meta.task_code || "").trim().toUpperCase();
  return code === "TC" && Boolean(meta.agenda_id);
}

/**
 * TC · Cita: el «vencimiento» es el fin de la visita (pasa a T4 Cualificar).
 * No es atraso del agente → nunca columna/estilo rojo por ese due.
 */
export function isTcAgendaVisitTask(tarea = {}) {
  return isFutureVisitBoardTask(tarea);
}

function earliestValidDate(...candidates) {
  const valid = candidates
    .map((value) => (value ? new Date(value) : null))
    .filter((d) => d && !Number.isNaN(d.getTime()));
  if (!valid.length) return null;
  return new Date(Math.min(...valid.map((d) => d.getTime())));
}

/** Captación creada a mano (sin flujo T). */
export function isCaptacionManualBoardTask(tarea = {}) {
  if (!tarea?.captacion_id) return false;
  if (tarea.lead_id) return false;
  const meta = parseTaskMetadata(tarea);
  if (meta.flow_id === "captacion_incoming_v1" || meta.captacion_flow === true) return false;
  if (String(meta.task_code || "").trim()) return false;
  const origen = String(tarea.origen || meta.source || "").toLowerCase();
  return (
    origen.includes("manual_captacion")
    || origen.includes("modal_crear_tarea_captacion")
  );
}

export function getTaskScheduleWindow(tarea = {}) {
  const meta = parseTaskMetadata(tarea);
  const fin = tarea.fecha_vencimiento ? new Date(tarea.fecha_vencimiento) : null;
  const created = parseTaskCreatedAt(tarea);
  const storedInicio = tarea.fecha_inicio ? new Date(tarea.fecha_inicio) : null;
  const anchorRaw = meta.task_due_anchor_at || meta.plazo_inicio_at;
  const anchor = anchorRaw ? new Date(anchorRaw) : null;

  let inicio = null;
  if (isFutureVisitBoardTask(tarea)) {
    inicio =
      storedInicio ||
      (meta.scheduled_start_at || meta.scheduledStartAt
        ? new Date(meta.scheduled_start_at || meta.scheduledStartAt)
        : null) ||
      created;
  } else if (
    storedInicio &&
    !Number.isNaN(storedInicio.getTime()) &&
    (isCaptacionManualBoardTask(tarea) || isScheduledExecutionTask(tarea))
  ) {
    // Recordatorios / programadas: respetar fecha_inicio futura (p. ej. en 6 meses).
    inicio = storedInicio;
  } else if (created && !Number.isNaN(created.getTime())) {
    // Tareas inmediatas (T1, T1b, T1c…): la ventana empieza al crearse, no en el vencimiento.
    inicio = created;
    if (
      storedInicio &&
      !Number.isNaN(storedInicio.getTime()) &&
      !isScheduledStartAfterCreation(storedInicio, created) &&
      storedInicio.getTime() < created.getTime()
    ) {
      inicio = storedInicio;
    }
  } else {
    inicio = earliestValidDate(storedInicio, anchor);
  }

  if (!inicio && created) inicio = created;
  return { inicio, fin };
}

/** Pendiente/en_curso dentro de la ventana inicio…vencimiento (días Madrid, fin con hora). */
export function isTaskActiveInScheduleWindow(tarea = {}, nowMs = Date.now()) {
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado !== "pendiente" && estado !== "en_curso") return false;
  const { inicio, fin } = getTaskScheduleWindow(tarea);
  if (!inicio || Number.isNaN(inicio.getTime())) return false;

  if (compareMadridDays(nowMs, inicio) < 0) return false;

  if (fin && !Number.isNaN(fin.getTime())) {
    const dayCmp = compareMadridDays(nowMs, fin);
    if (dayCmp > 0) return false;
    if (dayCmp === 0 && nowMs > fin.getTime()) return false;
  }

  return true;
}

/** Fecha de referencia para columnas futuras (solo antes de que empiece la ventana). */
export function getTaskColumnAnchorDate(tarea = {}, nowMs = Date.now()) {
  const { inicio, fin } = getTaskScheduleWindow(tarea);
  if (inicio && !Number.isNaN(inicio.getTime()) && compareMadridDays(nowMs, inicio) < 0) {
    return inicio;
  }
  // Ventana ya iniciada: no usar vencimiento como ancla (evita Próxima semana en tareas activas).
  if (inicio && !Number.isNaN(inicio.getTime()) && compareMadridDays(nowMs, inicio) >= 0) {
    return inicio;
  }
  if (fin && !Number.isNaN(fin.getTime())) return fin;
  if (inicio && !Number.isNaN(inicio.getTime())) return inicio;
  return null;
}

function columnKeyFromDate(value, nowMs = Date.now()) {
  if (!value) return "sin_fecha";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "sin_fecha";

  const hoyKey = madridDayKey(nowMs);
  const dayKey = madridDayKey(d);
  if (!hoyKey || !dayKey) return "sin_fecha";
  if (dayKey < hoyKey) return "atrasadas";
  if (dayKey === hoyKey) return "hoy";

  const mananaKey = addMadridDays(nowMs, 1);
  if (dayKey === mananaKey) return "manana";

  const finSemanaKey = addMadridDays(nowMs, 7 - madridWeekday(nowMs));
  const finProxSemanaKey = addMadridDays(finSemanaKey, 7);
  if (dayKey <= finSemanaKey) return "esta_semana";
  if (dayKey <= finProxSemanaKey) return "proxima_semana";
  return "siguientes";
}

/** Columna del tablero Kanban (días Madrid). */
export function getTaskBoardColumnKey(tarea = {}, nowMs = Date.now()) {
  if (isCaptacionManualBoardTask(tarea)) {
    const { inicio, fin } = getTaskScheduleWindow(tarea);
    const estado = String(tarea.estado || "").toLowerCase();
    const activa = estado === "pendiente" || estado === "en_curso";

    if (fin && !Number.isNaN(fin.getTime()) && nowMs > fin.getTime() && activa) {
      return "atrasadas";
    }
    // Antes de empezar: columna según fecha de inicio (Mañana / … / Siguientes)
    if (inicio && !Number.isNaN(inicio.getTime()) && compareMadridDays(nowMs, inicio) < 0) {
      return columnKeyFromDate(inicio, nowMs);
    }
    // Ya en ventana: columna según vencimiento
    return columnKeyFromDate(fin || inicio, nowMs);
  }

  const { inicio, fin } = getTaskScheduleWindow(tarea);
  const estado = String(tarea.estado || "").toLowerCase();
  const activa = estado === "pendiente" || estado === "en_curso";

  if (!inicio && !fin && !getTaskColumnAnchorDate(tarea, nowMs)) return "sin_fecha";

  // TC: fin de cita = momento de cualificar (T4), no «atrasada» en rojo.
  if (isTcAgendaVisitTask(tarea) && fin && !Number.isNaN(fin.getTime()) && nowMs > fin.getTime() && activa) {
    return "hoy";
  }

  if (fin && !Number.isNaN(fin.getTime()) && nowMs > fin.getTime() && activa) {
    return "atrasadas";
  }

  // Con vencimiento: mientras la ventana inicio…fin esté abierta → Hoy (leads T1/T2…).
  // Sin vencimiento: no forzar Hoy. Si no, citas TC / multimedia con solo fecha_inicio
  // pasada quedaban eternamente en Hoy en lugar de Atrasadas.
  if (
    fin &&
    !Number.isNaN(fin.getTime()) &&
    isTaskActiveInScheduleWindow(tarea, nowMs)
  ) {
    return "hoy";
  }

  const anchor = getTaskColumnAnchorDate(tarea, nowMs);
  if (!anchor || Number.isNaN(anchor.getTime())) return "sin_fecha";

  const hoyKey = madridDayKey(nowMs);
  const anchorKey = madridDayKey(anchor);
  if (!hoyKey || !anchorKey) return "sin_fecha";

  if (anchorKey < hoyKey) return "atrasadas";
  if (anchorKey === hoyKey) return "hoy";

  const mananaKey = addMadridDays(nowMs, 1);
  if (anchorKey === mananaKey) return "manana";

  const finSemanaKey = addMadridDays(nowMs, 7 - madridWeekday(nowMs));
  const finProxSemanaKey = addMadridDays(finSemanaKey, 7);
  if (anchorKey <= finSemanaKey) return "esta_semana";
  if (anchorKey <= finProxSemanaKey) return "proxima_semana";
  return "siguientes";
}

/**
 * Pendiente/en_curso visibles en el tablero.
 * Captación manual: también las de inicio futuro lejano → columna «Siguientes»
 * (todo lo posterior a «Próxima semana»). Ampliar, no ocultar.
 */
export function isTareaVisibleEnTablero(tarea = {}) {
  const estado = String(tarea.estado || "").toLowerCase();
  if (estado === "completada" || estado === "cancelada") return true;

  if (isFutureVisitBoardTask(tarea)) return true;

  if (isCaptacionManualBoardTask(tarea)) {
    // Siempre visibles: getTaskBoardColumnKey las coloca en Mañana / Esta semana /
    // Próxima semana / Siguientes según la fecha de inicio.
    return true;
  }

  const { inicio, fin } = getTaskScheduleWindow(tarea);
  const now = Date.now();
  if (inicio && !Number.isNaN(inicio.getTime()) && compareMadridDays(now, inicio) < 0) return false;
  if (fin && !Number.isNaN(fin.getTime())) {
    const dayCmp = compareMadridDays(now, fin);
    if (dayCmp > 0) return true;
    if (dayCmp === 0 && now > fin.getTime()) {
      return estado === "pendiente" || estado === "en_curso";
    }
  }
  return true;
}
